<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, reactive, ref, toRaw, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { v4 as uuid } from "uuid";

// Reuse old sub-components — no need to duplicate them
import Toolbar        from "../../../views/document/toolbar/Toolbar.vue";
import AnnotationPaintProperties from "../../../views/document/paint-properties/AnnotationPaintProperties.vue";
import SmartScrollBar from "../../../views/document/SmartScrollBar.vue";
import SearchWithinDocument from "../../../views/document/search-within-document.vue";
import AnnotationPanel from "../../../views/common/AnnotationPanel/APMain.vue";
import StampsManager  from "../../../views/document/stamps-manager/StampsManager.vue";
import CreateStampDialog from "../../../views/common/modals/stamps/CreateStampDialog.vue";
import AudioRecorder  from "../../../views/document/AudioRecorder.vue";
import AudioPlayer    from "../../../views/document/AudioPlayer.vue";
import DocumentDetailsTab from "../../components/ui/DocumentDetailsTab.vue";

import ProjectService    from "../../../service/projectService";
import AnnotationService from "../../../service/annotationService";
import { convertAudioToM4a } from "../../../service/audioConverterService";
import { mutations, getters } from "../../../store/index.js";
import { AP_CACHE_SET, state as APState } from "../../../store/annotationPanel";
import { ANNO_TYPES, BROADCAST_MESSAGE, ERROR_TOAST, BUTTON_ROLES, PUBLIC_VIEWER, VIEWER } from "../../../core/constants.js";
import { useTopBar } from "../../composables/useTopBar.js";
import { hasPermission, hasAnyPermission, COLLABORATOR_PERMISSIONS } from "../../composables/usePermissions.js";

// ── Injections ───────────────────────────────────────────────────────────────
const $fetch = inject("$fetch");
const route  = useRoute();
const router = useRouter();
const { set: setTopBar } = useTopBar();

// ── Services ─────────────────────────────────────────────────────────────────
const projectService    = new ProjectService();
const annotationService = new AnnotationService();

// ── Route params ─────────────────────────────────────────────────────────────
// v2 router uses :docId; old router used :documentId
const projectId  = route.params.projectId;
const documentId = route.params.docId;

// ── FoliaPdfViewer ───────────────────────────────────────────────────────────
const { FoliaPDFViewer } = window.FoliaPdfViewer;
const foliaPdfViewer = new FoliaPDFViewer();
window.foliaPdfViewer = foliaPdfViewer;

// ── Automerge handle — declared here so all handlers in this setup share it ──
let handle = null;

// ── Automerge repo ───────────────────────────────────────────────────────────
const repo = toRaw(getters.automergeRepo);

// ── DOM refs ──────────────────────────────────────────────────────────────────
const rootEl = ref(null);
const smartScrollBar = ref(null);

// ── Reactive state ────────────────────────────────────────────────────────────
const isLoading             = ref(false);
const pageFullyLoaded       = ref(false);
const isDocumentDataLoading = ref(true);
const connectionLost        = ref(false);
const documentFullyDownloaded = ref(false);

const currentPage = ref(1);
const countPages  = ref(0);
const zoomValue   = ref(100);
const canAnnotate = ref(false);
const canUndo     = ref(false);
const canRedo     = ref(false);
const sidePanelTab = ref("annotations"); // annotations | details
// The document's parent project (name, isSingleton) — fetched alongside the
// document itself purely so the Share modal can pick the right resource name
// (see TopBar.vue's onShareClick): the document's own name for a singleton
// project's lone document, the project's name for a real multi-doc project.
const projectInfo = ref(null);

const currentDocument = reactive({
  document: null,
  permissions: [],
  objects: [],
  userRole: VIEWER,
  userName: "",
  userEmail: "",
  documentName: "",
  projectName: "",
  meta: {},
});

const currentCollaborators = reactive({ collaborators: {} });

const selectedAnnotations = reactive({
  drawingProps: null,
  parentRects: null,
  canDelete: false,
  info: {},
  annotationTypes: new Set(),
  annotationIds: new Set(),
  hasError: false,
});

const stampsManagerState = reactive({ shown: false });
const createStampState   = reactive({ showDialog: false, data: {} });
const audioRecorderState = reactive({ shown: false, annotationId: null, page: null });
const audioPlayerState   = reactive({
  shown: false, annotationId: null, objectUrl: null, isLoading: false, errorMsg: "", duration: 0,
});

const pendingAudioMimeTypes = new Map();
const pendingAudioDurations = new Map();

// ── Computed ──────────────────────────────────────────────────────────────────
const canManageTools = computed(() =>
  currentDocument.permissions.includes("MANAGE_ANNOTATION") ||
  currentDocument.permissions.includes("MANAGE_OWN_COMMENT")
);

// ── Mount ─────────────────────────────────────────────────────────────────────
onMounted(async () => {
  await getDocumentInfo();

  if (!canAnnotate.value) {
    isDocumentDataLoading.value = false;
    return;
  }

  isLoading.value = true;
  fetchCollaborators(projectId).catch(console.error);
  mutations.toggleUploadMonitorShown(false);

  $fetch.on(BROADCAST_MESSAGE.DOCUMENT_UPDATED, documentUpdated);
  $fetch.on(BROADCAST_MESSAGE.PROJECT_UPDATED,  projectUpdated);

  // DOM refs required by FoliaPdfViewer — must be queried after mount
  const ui = {
    container:     rootEl.value.querySelector("#viewerContainer"),
    viewer:        rootEl.value.querySelector("#viewer"),
    thumbnailView: rootEl.value.querySelector(".thumbnail-pages"),
  };

  const { userEmail, userName, userRole, permissions } = await getDocumentData();

  // Now that the document's real permissions are known, refine the download
  // popover's options — it opened with both hidden (see getDocumentInfo).
  setTopBar({
    title: currentDocument.documentName || "Document",
    showShare: hasPermission(permissions, COLLABORATOR_PERMISSIONS.SHARE_PROJECT),
    showDownload: true,
    canDownloadAnnotated: hasPermission(permissions, COLLABORATOR_PERMISSIONS.DOWNLOAD_ANNOTATED_DOCUMENT),
    canDownloadOriginal: hasAnyPermission(permissions, [COLLABORATOR_PERMISSIONS.DOWNLOAD_SOURCE_DOCUMENT, COLLABORATOR_PERMISSIONS.DOWNLOAD_ORIGINAL_DOCUMENT]),
    project: projectInfo.value,
  });

  const dataProxy = {
    get userEmail()   { return userEmail.toLowerCase(); },
    get userName()    { return userName; },
    get userRole()    { return userRole; },
    get documentId()  { return documentId; },
    get permissions() { return permissions; },
  };

  await foliaPdfViewer.init(ui, dataProxy);

  // Subscribe to all PDF viewer events
  foliaPdfViewer.eventBus.on("documentloaded",           onDocumentLoaded);
  foliaPdfViewer.eventBus.on("pagechanging",             onPageChanging);
  foliaPdfViewer.eventBus.on("scalechanging",            onScaleChanging);
  foliaPdfViewer.eventBus.on("floatingbarhide",          onHideFloatingBar);
  foliaPdfViewer.eventBus.on("floatingbarshow",          onShowFloatingBar);
  foliaPdfViewer.eventBus.on("pagesloaded",              onPagesLoaded);
  foliaPdfViewer.eventBus.on("stop-drawing",             onStopDrawing);
  foliaPdfViewer.eventBus.on("undo-redo-changed",        onUndoRedoChanged);
  foliaPdfViewer.eventBus.on("set-replies-read-status",  onSetRepliesReadStatus);
  foliaPdfViewer.eventBus.on("folia-data",               getObjects);
  foliaPdfViewer.eventBus.on("objects-were-updated",     objectsWereUpdated);
  foliaPdfViewer.eventBus.on("make-as-a-stamp",          createStampFromAnnotation);
  foliaPdfViewer.eventBus.on("attach-comment-for-annotation", handleCommentThread);
  foliaPdfViewer.eventBus.on("comment-attached",         afterCommentAttachedToAnnotation);
  foliaPdfViewer.eventBus.on("audio-annotation-placed",  handleAudioAnnotationPlaced);
  foliaPdfViewer.eventBus.on("audio-annotation-clicked", handleAudioAnnotationClicked);

  // The inline play button on the audio annotation widget (rendered by the
  // pdf-viewer bundle itself) dispatches "audio-src-requested" and awaits this
  // provider to resolve an object URL — without it, its spinner never resolves.
  foliaPdfViewer.setAudioProvider(async (blobId) => {
    const result = await annotationService.downloadAudioBlob(blobId);
    if (!result?.content) return null;
    const byteStr = atob(result.content);
    const ab = new ArrayBuffer(byteStr.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
    const annotation = currentDocument.objects.find((o) => o.blobId === blobId);
    const mimeType = annotation?.audioMimeType ?? "audio/webm";
    return URL.createObjectURL(new Blob([ab], { type: mimeType }));
  });

  await initializeAutomerge();

  openDocument(documentId).catch(console.error);

  document.addEventListener("wheel", onWheelListener, { passive: false });
  pageFullyLoaded.value = true;
  isDocumentDataLoading.value = false;
});

// ── Unmount cleanup ───────────────────────────────────────────────────────────
onBeforeUnmount(() => {
  // Navigating away while onMounted is still awaiting getDocumentInfo/
  // getDocumentData/foliaPdfViewer.init() (e.g. clicking another file before
  // this one finishes loading) fires this before the viewer is ready.
  // foliaPdfViewer's internal pdfViewer/eventBus are only set up inside
  // init(), so calling stopDrawing()/etc. before then throws (they're a
  // vendored, unpatchable bundle — see folia-pdf-viewer.js's stopDrawing).
  // pageFullyLoaded only flips true once init() and all listener
  // registration below have completed, so it's the right readiness check.
  if (pageFullyLoaded.value) {
    foliaPdfViewer.stopDrawing();
    foliaPdfViewer.resetObjectsSeletion();

    foliaPdfViewer.eventBus.off("documentloaded",           onDocumentLoaded);
    foliaPdfViewer.eventBus.off("pagechanging",             onPageChanging);
    foliaPdfViewer.eventBus.off("scalechanging",            onScaleChanging);
    foliaPdfViewer.eventBus.off("floatingbarhide",          onHideFloatingBar);
    foliaPdfViewer.eventBus.off("floatingbarshow",          onShowFloatingBar);
    foliaPdfViewer.eventBus.off("pagesloaded",              onPagesLoaded);
    foliaPdfViewer.eventBus.off("stop-drawing",             onStopDrawing);
    foliaPdfViewer.eventBus.off("undo-redo-changed",        onUndoRedoChanged);
    foliaPdfViewer.eventBus.off("folia-data",               getObjects);
    foliaPdfViewer.eventBus.off("objects-were-updated",     objectsWereUpdated);
    foliaPdfViewer.eventBus.off("make-as-a-stamp",          createStampFromAnnotation);
    foliaPdfViewer.eventBus.off("attach-comment-for-annotation", handleCommentThread);
    foliaPdfViewer.eventBus.off("comment-attached",         afterCommentAttachedToAnnotation);
    foliaPdfViewer.eventBus.off("audio-annotation-placed",  handleAudioAnnotationPlaced);
    foliaPdfViewer.eventBus.off("audio-annotation-clicked", handleAudioAnnotationClicked);

    foliaPdfViewer.deinit();
  }

  document.removeEventListener("wheel", onWheelListener, { passive: false });
  $fetch.off(BROADCAST_MESSAGE.PROJECT_UPDATED,  projectUpdated);
  $fetch.off(BROADCAST_MESSAGE.DOCUMENT_UPDATED, documentUpdated);
});

// ── Data loading ──────────────────────────────────────────────────────────────
async function getDocumentInfo() {
  isDocumentDataLoading.value = true;
  try {
    const [docData, projectData] = await Promise.all([
      projectService.getDocument({ documentId }),
      projectService.getProject({ projectId }),
    ]);
    currentDocument.documentName = docData.name ?? "Document";
    currentDocument.meta = { ...docData };
    canAnnotate.value = docData.isAnnotatable ?? true;
    projectInfo.value = projectData;
    // showShare stays hidden until permissions are known (see onMounted below)
    // so the button doesn't flash for viewers/contributors then disappear.
    setTopBar({ title: docData.name ?? "Document", showDownload: true, project: projectData });
  } catch (err) {
    console.error("getDocumentInfo error", err);
    // Default to annotatable so the viewer still shows during dev
    canAnnotate.value = true;
    setTopBar({ title: "Document", showDownload: true });
  }
}

async function getDocumentData() {
  try {
    const data = await annotationService.getDocumentData({ documentId, projectId });
    currentDocument.document    = { id: documentId, projectId };
    currentDocument.permissions = data.permissions ?? [];
    currentDocument.userEmail   = data.userEmail ?? "";
    currentDocument.userName    = data.userName ?? "";
    currentDocument.userRole    = data.userRole ?? VIEWER;
    // Seeds localStorage["tool-presets"] from the backend — Toolbar.vue
    // (reused as-is here) reads that key for the user's saved tool presets.
    await annotationService.getToolPreset({});
    return data;
  } catch (err) {
    console.error("getDocumentData error", err);
    // Fall back to minimal data so the viewer still initialises
    currentDocument.document  = { id: documentId, projectId };
    currentDocument.permissions = ["MANAGE_ANNOTATION", "MANAGE_OWN_COMMENT"];
    currentDocument.userRole    = VIEWER;
    return { permissions: currentDocument.permissions, userEmail: "", userName: "", userRole: VIEWER };
  }
}

async function fetchCollaborators(pid) {
  const { collaborators = [] } = await projectService.getProjectCollaborators({ projectId: pid });
  collaborators.forEach((c) => { currentCollaborators.collaborators[c.email] = c; });
}

// ── Automerge ─────────────────────────────────────────────────────────────────
async function initializeAutomerge() {
  try {
    const docId = await projectService.getDocumentAnnotationDocId({ documentId });
    if (!docId) {
      connectionLost.value = true;
      return;
    }
    handle = repo.find(docId);
    window.handle = handle;

    repo.networkSubsystem.on("message", () => {
      handle.docSync();
      foliaPdfViewer.refreshFoliaLayers();
      foliaPdfViewer.eventBus.dispatch("refresh-folia-page");
    });
  } catch (err) {
    console.error("initializeAutomerge error", err);
    connectionLost.value = true;
  }
}

// ── PDF open ──────────────────────────────────────────────────────────────────
async function openDocument(docId) {
  documentFullyDownloaded.value = false;
  try {
    const { content } = await projectService.downloadDocumentForPDFJSV2({ documentId: docId });
    if (content) {
      await foliaPdfViewer.close();
      await foliaPdfViewer.open(content);
    }
  } catch (err) {
    console.error("openDocument error", err);
    $fetch.dispatch(BROADCAST_MESSAGE.TOAST, {
      type: ERROR_TOAST, title: "Error opening document", message: err.message,
    });
  } finally {
    documentFullyDownloaded.value = true;
    isLoading.value = false;
  }
}

// ── Annotation read (folia-data event) ───────────────────────────────────────
function groupBy(arr, key) {
  return arr.reduce((g, item) => {
    (g[item[key]] = g[item[key]] || []).push(item);
    return g;
  }, {});
}

async function getObjects({ responseName, reqProps: { pageNumber } }) {
  try {
    const annotationsResponse = await annotationService.getAnnotations(
      { projectId, documentId },
      handle,
    );

    const replies = groupBy(
      annotationsResponse.filter((r) => r.__typename === "ReplyAnnotation"),
      "commentId",
    );

    const annotations = annotationsResponse
      .filter((a) => a.__typename !== "ReplyAnnotation")
      .map((ann) => {
        if (replies[ann.id]) ann.replies = replies[ann.id];
        const collab = currentCollaborators.collaborators[ann.collaboratorEmail];
        ann.collaboratorName = collab ? collab.name : ann.collaboratorEmail;
        return ann;
      });

    // Sync into local object list for SmartScrollBar / AP panel
    annotationsResponse.forEach((o) => {
      const idx = currentDocument.objects.findIndex((co) => co.id === o.id);
      if (idx > -1) currentDocument.objects[idx] = o;
      else currentDocument.objects.push(o);
    });
    currentDocument.objects = currentDocument.objects.filter(
      (o) => !o.deletedAt && o.__typename !== "ReplyAnnotation",
    );

    AP_CACHE_SET.setCurrentPanel({
      panel: "ANNOTATIONS",
      data: {
        permissions: currentDocument.permissions,
        userEmail:   currentDocument.userEmail,
        userName:    currentDocument.userName,
        objects:     currentDocument.objects,
      },
    });

    foliaPdfViewer.eventBus.dispatch(responseName, {
      permissions: currentDocument.permissions,
      userEmail:   currentDocument.userEmail,
      userName:    currentDocument.userName,
      objects:     annotations.filter((o) => o.page === pageNumber),
    });
  } catch (err) {
    console.error("getObjects error", err);
  }
}

// ── Annotation write (objects-were-updated event) ─────────────────────────────
async function objectsWereUpdated(objectsData) {
  const { id: docId } = currentDocument.document;
  for (const objectData of objectsData) {
    console.log(objectData);

    if (objectData.__typename === ANNO_TYPES.COMMENT) {
      delete objectData.replies;
      // Cascade the comment's deletedAt onto its replies — covers both
      // deleting the comment and restoring it via pdf.js undo.
      const repliesDeletedTimestamp = objectData.deletedAt && objectData.deletedAt !== ""
        ? new Date(objectData.deletedAt).getTime()
        : 0;
      handle.change((d) => {
        Object.values(d.annotations).forEach((annotation) => {
          const kind = annotation.kind?.val ?? annotation.kind;
          const reference = annotation.reference?.val ?? annotation.reference;
          if (kind === "reply" && reference === objectData.id) {
            annotation.deleted = repliesDeletedTimestamp;
          }
        });
      });
    }

    // Re-anchor any attached comment when parent moves or gets deleted
    currentDocument.objects.forEach((co) => {
      if (!co.parentAnnotationId || co.parentAnnotationId !== objectData.id) return;
      if (objectData.deletedAt && objectData.deletedAt !== "") {
        foliaPdfViewer.eventBus.dispatch("delete-comment-annotation", {
          parentAnnotationId: objectData.id, commentId: co.id,
        });
      } else {
        const yOff = -0.03;
        let anchor;
        switch (objectData.__typename) {
          case "SquareAnnotation":
          case "CircleAnnotation":
          case "TextBoxAnnotation":
          case "ImageAnnotation":
            anchor = [objectData.rect[2], objectData.rect[1] + yOff]; break;
          case "ArrowAnnotation":
            anchor = [objectData.sourcePoint[0], objectData.sourcePoint[1] + yOff]; break;
          case "InkAnnotation":
            anchor = [objectData.paths[0][0], objectData.paths[0][1]]; break;
          case "AudioAnnotation":
            anchor = [objectData.anchorPoint[0] + 0.03, objectData.anchorPoint[1] + yOff]; break;
        }
        if (anchor) {
          foliaPdfViewer.eventBus.dispatch("move-comment-annotation", {
            anchorPoint: anchor, annotationObject: co,
          });
        }
      }
    });

    // Skip cancelled audio placements (no blobId yet)
    if (objectData.__typename === "AudioAnnotation" && !objectData.blobId) continue;

    if (objectData.__typename === "AudioAnnotation" && pendingAudioMimeTypes.has(objectData.id)) {
      objectData.audioMimeType = pendingAudioMimeTypes.get(objectData.id);
      pendingAudioMimeTypes.delete(objectData.id);
    }
    if (objectData.__typename === "AudioAnnotation" && pendingAudioDurations.has(objectData.id)) {
      objectData.audioLength = pendingAudioDurations.get(objectData.id);
      pendingAudioDurations.delete(objectData.id);
    }

    try {
      await annotationService.createAnnotation(projectId, docId, objectData, handle);
    } catch (err) {
      console.error("createAnnotation error", err);
    }
  }
}

// ── Broadcast events ──────────────────────────────────────────────────────────
function projectUpdated(pid) {
  if (pid !== projectId) return;
  // Could refresh doc list or metadata — extend when needed
}
function documentUpdated() {
  foliaPdfViewer.eventBus.dispatch("refresh-folia-page");
}

// ── Comment thread ────────────────────────────────────────────────────────────
async function handleCommentThread({ objects: annotationDataObjects }) {
  const { bounds, id: parentAnnotationId } = annotationDataObjects[0];
  const pageNumber = currentPage.value > 0 ? currentPage.value - 1 : 0;
  useTool("COMMENT", { bounds, parentAnnotationId, pageNumber });
}

function afterCommentAttachedToAnnotation() { /* no-op */ }

// ── Audio annotation ──────────────────────────────────────────────────────────
function handleAudioAnnotationPlaced({ annotationId, page }) {
  foliaPdfViewer.stopDrawing();
  audioRecorderState.annotationId = annotationId;
  audioRecorderState.page         = page;
  audioRecorderState.shown        = true;
}

async function handleAudioAnnotationClicked({ annotationId, blobId }) {
  audioPlayerState.annotationId = annotationId;
  audioPlayerState.objectUrl    = null;
  audioPlayerState.errorMsg     = "";
  audioPlayerState.isLoading    = true;
  audioPlayerState.shown        = true;
  // Read straight off the live automerge doc — currentDocument.objects is only
  // refreshed on the next "folia-data" event, so it can still be stale here.
  audioPlayerState.duration     = handle?.docSync()?.annotations?.[annotationId]?.audioLength ?? 0;

  if (!blobId) {
    audioPlayerState.isLoading = false;
    audioPlayerState.errorMsg  = "No audio recorded yet.";
    return;
  }
  try {
    const blobData = await annotationService.downloadAudioBlob(blobId);
    if (!blobData?.content) { audioPlayerState.errorMsg = "Could not load audio."; return; }
    const byteStr = atob(blobData.content);
    const ab = new ArrayBuffer(byteStr.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
    const mimeType =
      currentDocument.objects.find((o) => o.id === annotationId)?.audioMimeType ?? "audio/webm";
    audioPlayerState.objectUrl = URL.createObjectURL(new Blob([ab], { type: mimeType }));
  } catch (err) {
    audioPlayerState.errorMsg = "Failed to load audio.";
    console.error("Audio download error", err);
  } finally {
    audioPlayerState.isLoading = false;
  }
}

async function onAudioSaved({ base64, mimeType, durationSeconds }) {
  audioRecorderState.shown = false;
  const { annotationId } = audioRecorderState;
  try {
    const converted = await convertAudioToM4a(base64, mimeType);
    const blobId = await annotationService.uploadAudioBlob(documentId, annotationId, converted.base64);
    pendingAudioMimeTypes.set(annotationId, converted.mimeType);
    pendingAudioDurations.set(annotationId, durationSeconds);
    foliaPdfViewer.updateAnnotation({ id: annotationId, blobId, audioLength: durationSeconds });
  } catch (err) {
    console.error("Audio upload error", err);
    $fetch.dispatch(BROADCAST_MESSAGE.TOAST, {
      type: ERROR_TOAST, title: "Failed to save audio", message: err.message,
    });
    currentDocument.objects = currentDocument.objects.filter((o) => o.id !== annotationId);
    if (handle) {
      handle.change((d) => { delete d.annotations[annotationId]; });
    }
    foliaPdfViewer.deleteAnnotationById(annotationId);
    audioRecorderState.annotationId = null;
  }
}

function onAudioRecorderCancel() {
  const { annotationId } = audioRecorderState;
  audioRecorderState.shown        = false;
  audioRecorderState.annotationId = null;
  if (!annotationId) return;
  currentDocument.objects = currentDocument.objects.filter((o) => o.id !== annotationId);
  foliaPdfViewer.deleteAnnotationById(annotationId);
}

function onAudioPlayerClose() {
  if (audioPlayerState.objectUrl) URL.revokeObjectURL(audioPlayerState.objectUrl);
  audioPlayerState.shown     = false;
  audioPlayerState.objectUrl = null;
  audioPlayerState.errorMsg  = "";
}

// ── Stamp handling ────────────────────────────────────────────────────────────
async function createStampFromAnnotation({ id: annotationId }) {
  const all = await annotationService.getAnnotations({ projectId, documentId }, handle);
  const selected = all.find((a) => a.id === annotationId);
  if (selected) { createStampState.showDialog = true; createStampState.data = selected; }
}

function useStamp(stampData) {
  const stamp   = { ...stampData.stamp, id: uuid(), addedAt: new Date().toISOString() };
  stampsManagerState.shown = false;
  foliaPdfViewer.startDrawing("STAMPS", stamp);
}

// ── PDF viewer event handlers ─────────────────────────────────────────────────
function onDocumentLoaded(e) {
  currentPage.value = e.source.page;
  countPages.value  = e.source.pagesCount;
  zoomValue.value   = parseInt(e.source.zoom, 10);
  isLoading.value   = false;
  setTimeout(() => {
    const { page, object } = route.query;
    if (page && !object)   foliaPdfViewer.page = parseInt(page, 10);
    else if (page && object) foliaPdfViewer.selectAnnotationObject(page, object);
  }, 10);
}

function onPageChanging(e)  { currentPage.value = e.pageNumber; }
function onScaleChanging(e) { zoomValue.value = parseInt(e.scale * 100, 10); }
function onStopDrawing()    { resetSelection(); }
function onUndoRedoChanged(s) { canUndo.value = s.canUndo; canRedo.value = s.canRedo; }
function onPagesLoaded()    { foliaPdfViewer.checkForNativeAnnotationsPresence().catch(() => {}); }

function onSetRepliesReadStatus(statuses = []) {
  if (!statuses.length) return;
  const { id: docId } = currentDocument.document;
  $fetch.setRepliesReadStatus(
    statuses.map((s) => ({ documentId: docId, projectId, ...s })),
  );
}

// ── Floating annotation properties bar ────────────────────────────────────────
function scrollHandlerWhileToolbarShown() {
  if (window._floatingBarObjects) onShowFloatingBar({ objects: window._floatingBarObjects });
}

function resetSelection() {
  selectedAnnotations.drawingProps   = null;
  selectedAnnotations.parentRects    = null;
  selectedAnnotations.canDelete      = false;
  selectedAnnotations.info           = {};
  selectedAnnotations.hasError       = false;
  selectedAnnotations.annotationTypes = new Set();
  selectedAnnotations.annotationIds  = new Set();
}

function onHideFloatingBar() {
  document.querySelector("#viewerContainer")
    ?.removeEventListener("scroll", scrollHandlerWhileToolbarShown);
  delete window._floatingBarObjects;
  resetSelection();
}

function onShowFloatingBar({ objects }) {
  window._floatingBarObjects = objects;
  document.querySelector("#viewerContainer")
    ?.addEventListener("scroll", scrollHandlerWhileToolbarShown);

  // CommentAnnotation selected — hide the bar
  if (objects.some((o) => o.annotationRawData.__typename === "CommentAnnotation")) {
    return onHideFloatingBar();
  }

  resetSelection();

  // Compute bounding rect across all selected annotations
  let pageEl;
  const annoRect = objects.reduce(
    (acc, obj) => {
      pageEl = obj.foliaLayer;
      const { clientWidth: w, clientHeight: h, offsetLeft: l, offsetTop: t } = obj.annotationDIV;
      return {
        left:   Math.min(acc.left,   l),
        top:    Math.min(acc.top,    t),
        right:  Math.max(acc.right,  l + w),
        bottom: Math.max(acc.bottom, t + h),
        width:  Math.max(acc.right,  l + w) - Math.min(acc.left,   l),
        height: Math.max(acc.bottom, t + h) - Math.min(acc.top,    t),
        scale:  obj.viewport.scale,
      };
    },
    { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity },
  );

  // Walk up to .document-container to accumulate offsets
  let parentEl = pageEl?.parentNode;
  let offsetLeft = 0, offsetTop = 0;
  const stopEl = document.querySelector(".document-container");
  do {
    offsetLeft += parentEl?.offsetLeft  || 0;
    offsetTop  += (parentEl?.offsetTop  || 0) - (parentEl?.scrollTop || 0);
    parentEl    = parentEl?.parentNode;
  } while (parentEl && parentEl !== stopEl);

  selectedAnnotations.parentRects = {
    pageRect: { width: pageEl?.clientWidth, height: pageEl?.clientHeight, offsetLeft, offsetTop },
    annoRect,
    annotData: objects.map((o) => ({ ...o.getRawData() })),
  };

  selectedAnnotations.drawingProps = objects.reduce(
    (acc, obj) => Object.assign(acc || {}, obj.editableProperties.get()),
    null,
  );
  selectedAnnotations.canDelete       = objects.every((o) => o.canDelete);
  selectedAnnotations.annotationTypes = objects.reduce((s, o) => s.add(o.annotationRawData.__typename), new Set());
  selectedAnnotations.annotationIds   = objects.reduce((s, o) => s.add(o.annotationRawData.id),        new Set());
  selectedAnnotations.hasError        = objects.some((o) => o.annotationRawData.error);

  $fetch.getProjectCollaborators(projectId).then(({ collaborators }) => {
    const authorEmails = Array.from(new Set(objects.map((o) => o.author)));
    const authorNames  = authorEmails.map((email) => {
      const u = collaborators.find((c) => c.email === email);
      return u?.profile?.username || email;
    });
    selectedAnnotations.info = {
      author: authorNames.length <= 3
        ? authorNames.join(", ")
        : authorNames.slice(0, 3).join(", ") + ` and ${authorNames.length - 3} more`,
      addedAt: objects.at(-1)?.addedAt,
    };
  }).catch(() => {});
}

// ── Tool / zoom controls ──────────────────────────────────────────────────────
function useTool(tool, preset = {}) {
  foliaPdfViewer.stopDrawing();
  foliaPdfViewer.resetObjectsSeletion();
  stampsManagerState.shown = false;

  if (tool === BUTTON_ROLES.STAMPS) {
    stampsManagerState.shown = true;
  } else if (tool === BUTTON_ROLES.AUDIO) {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach((t) => t.stop());
        foliaPdfViewer.startDrawing("AUDIO", {});
      })
      .catch(() => {
        $fetch.dispatch(BROADCAST_MESSAGE.TOAST, {
          type: ERROR_TOAST, title: "Microphone access required",
        });
        foliaPdfViewer.eventBus.dispatch("stop-drawing");
      });
  } else if (tool !== BUTTON_ROLES.POINTER) {
    foliaPdfViewer.startDrawing(tool, preset);
  }
}

function updateDrawingProperties(preset = {}) {
  if (selectedAnnotations.drawingProps) foliaPdfViewer.updateObjectsDrawingProperties(preset);
  else foliaPdfViewer.updateToolDrawingProperties(preset);
}

function deleteSelectedAnnotations() { foliaPdfViewer.deleteSelectedAnnotations(); }
function duplicateAnnot()            { foliaPdfViewer.duplicateSelectedAnnotations(); }
function undo()                      { foliaPdfViewer.undo(); }
function redo()                      { foliaPdfViewer.redo(); }
function zoomIn()                    { foliaPdfViewer.zoomIn(5); }
function zoomOut()                   { foliaPdfViewer.zoomOut(5); }

function closeStaticToolbar() {
  stampsManagerState.shown = false;
  foliaPdfViewer.resetObjectsSeletion();
}

// ── Ctrl+wheel zoom ───────────────────────────────────────────────────────────
let zoomTimerId = null;
let zoomFactor  = 1;

function onWheelListener(e) {
  if (!e.ctrlKey) return;
  e.preventDefault();
  resetSelection();
  clearTimeout(zoomTimerId);
  zoomFactor = parseFloat((zoomFactor + 0.025 * (e.deltaY < 0 ? 1 : -1)).toFixed(2));
  const viewerDiv = foliaPdfViewer.pdfViewer.container;
  viewerDiv.style.zoom = zoomFactor;
  zoomTimerId = setTimeout(() => {
    foliaPdfViewer.pdfViewer.currentScaleValue =
      foliaPdfViewer.pdfViewer.currentScale * zoomFactor;
    foliaPdfViewer.forceRendering();
    zoomFactor           = 1;
    viewerDiv.style.zoom = 1;
  }, 250);
}

// ── Annotation panel ──────────────────────────────────────────────────────────
watch(() => APState.isAPOpen, async () => {
  await nextTick();
  foliaPdfViewer.forceRendering();
});

function scrollToObject(data) {
  smartScrollBar.value?.scrollToAnnotation(data);
  foliaPdfViewer.selectObjectV2(data);
}
</script>

<template>
  <!-- .document-container is required by onShowFloatingBar DOM traversal -->
  <div class="document-container" ref="rootEl">

    <!-- ── Toolbar row ─────────────────────────────────────────────────── -->
    <div class="document-header border-b border-gray-200 dark:border-slate-200 bg-white dark:bg-slate-800">
      <Toolbar
        v-if="pageFullyLoaded"
        @useTool="useTool"
        :canManageTools="canManageTools"
      />
      <div v-else class="min-h-[52px]" />

      <StampsManager
        v-if="stampsManagerState.shown"
        @close="closeStaticToolbar"
        @useStamp="useStamp"
      />
      <CreateStampDialog
        v-if="createStampState.showDialog"
        :annotationData="createStampState.data"
        @close="createStampState.showDialog = false"
      />
      <AudioRecorder
        v-if="audioRecorderState.shown"
        @saved="onAudioSaved"
        @cancel="onAudioRecorderCancel"
      />
      <AudioPlayer
        v-if="audioPlayerState.shown"
        :objectUrl="audioPlayerState.objectUrl"
        :isLoading="audioPlayerState.isLoading"
        :errorMsg="audioPlayerState.errorMsg"
        :initialDuration="audioPlayerState.duration"
        @close="onAudioPlayerClose"
      />
    </div>

    <!-- ── Connection lost overlay ────────────────────────────────────── -->
    <div
      v-if="!isDocumentDataLoading && connectionLost"
      class="absolute inset-0 z-50 flex items-start justify-center pt-20 bg-slate-700/60"
    >
      <div class="rounded-xl bg-white shadow-xl p-6 max-w-sm w-full">
        <h2 class="text-base font-bold text-slate-800 mb-2">Connection lost</h2>
        <p class="text-sm text-slate-500 mb-5">
          Your changes might not be saved. Please reload to reconnect.
        </p>
        <button
          class="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-semibold hover:bg-slate-600 transition-colors cursor-pointer border-0"
          @click="() => window.location.reload()"
        >Reload</button>
      </div>
    </div>

    <!-- ── Not-annotatable detail card (no PDF viewer) ────────────────── -->
    <div
      v-else-if="!isDocumentDataLoading && !canAnnotate && documentFullyDownloaded"
      class="flex items-start justify-center pt-16 h-full bg-slate-50"
    >
      <div class="rounded-xl bg-white shadow p-6 max-w-sm w-full">
        <h2 class="text-base font-bold text-slate-800 mb-1">Details</h2>
        <p class="text-xs text-slate-400 mb-4">Uploaded by</p>
        <div class="flex items-center gap-3 mb-5">
          <div class="w-9 h-9 rounded-full bg-slate-400 flex items-center justify-center text-white text-xs font-semibold shrink-0">
            {{ currentDocument.meta?.nameTag }}
          </div>
          <div>
            <p class="text-sm font-medium text-slate-700">{{ currentDocument.meta?.userName }}</p>
            <p class="text-xs text-slate-400">{{ currentDocument.meta?.userEmail }}</p>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
          <span class="font-semibold">Date Uploaded</span>
          <span class="font-semibold">Last Modified</span>
          <span>{{ currentDocument.meta?.formattedCreatedAt }}</span>
          <span>{{ currentDocument.meta?.formattedUpdatedAt }}</span>
          <span class="font-semibold mt-2">File type</span>
          <span class="font-semibold mt-2">Size</span>
          <span>{{ currentDocument.meta?.originMimeType }}</span>
          <span>{{ currentDocument.meta?.formattedSourceSize }}</span>
        </div>
      </div>
    </div>

    <!-- ── Main document area ─────────────────────────────────────────── -->
    <div v-else class="document-main">
      <div class="thumbnail-pages" />
      <div class="pdf-viewer">

        <div id="viewerContainer" class="viewer-container">
          <div v-if="isLoading" class="viewer-loader" />
          <div id="viewer" class="document-viewer show-scrollbar" data-role="folia-viewer" />
        </div>

        <SmartScrollBar
          v-if="countPages > 0"
          ref="smartScrollBar"
          :pdf-viewer="foliaPdfViewer"
          :objects="currentDocument.objects"
          :selected-object="selectedAnnotations"
        />

        <SearchWithinDocument
          v-if="countPages > 0"
          :pdf-viewer="foliaPdfViewer"
        />

        <!-- Floating annotation properties bar -->
        <AnnotationPaintProperties
          v-if="selectedAnnotations.drawingProps"
          :drawingProps="selectedAnnotations.drawingProps"
          :parentRects="selectedAnnotations.parentRects"
          :canDelete="selectedAnnotations.canDelete"
          :canManage="canManageTools"
          :annotationTypes="selectedAnnotations.annotationTypes"
          :annotationIds="selectedAnnotations.annotationIds"
          :info="selectedAnnotations.info"
          :selectedAnnotations="selectedAnnotations"
          @updateDrawing="updateDrawingProperties"
          @deleteSelectedAnnotations="deleteSelectedAnnotations"
          @duplicateAnnot="duplicateAnnot"
        />

        <!-- Zoom controls -->
        <div class="document-zoom">
          <div class="zoom-btn" @click.stop="zoomOut">
            <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
              <path d="M9.167 1.167L.833 1.167" stroke="#334155" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </div>
          <span>{{ zoomValue }}%</span>
          <div class="zoom-btn" @click.stop="zoomIn">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M5 .833v8.334M9.167 5H.833" stroke="#334155" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </div>
        </div>

        <!-- Page counter -->
        <div class="document-pages">Page {{ currentPage }} / {{ countPages }}</div>

        <!-- Full-screen loading overlay while PDF downloads -->
        <div class="document-loading" v-if="!documentFullyDownloaded" />
      </div>

      <!-- Right side panel: Annotations / Details tabs -->
      <div v-if="APState.isAPOpen" class="flex flex-col min-w-80 bg-gray-50 dark:bg-slate-800 z-20 overflow-hidden">
        <div class="flex items-center gap-1 p-2 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700 shrink-0">
          <button
            :class="sidePanelTab === 'annotations'
              ? 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 font-semibold'
              : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'"
            class="px-3 py-1.5 rounded-lg text-[13px] border-0 bg-transparent cursor-pointer transition-colors"
            @click="sidePanelTab = 'annotations'"
          >
            Annotations
          </button>
          <button
            :class="sidePanelTab === 'details'
              ? 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 font-semibold'
              : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'"
            class="px-3 py-1.5 rounded-lg text-[13px] border-0 bg-transparent cursor-pointer transition-colors"
            @click="sidePanelTab = 'details'"
          >
            Details
          </button>
        </div>

        <div class="flex-1 overflow-y-auto">
          <AnnotationPanel v-show="sidePanelTab === 'annotations'" @scrollToObject="scrollToObject" />
          <DocumentDetailsTab
            v-if="sidePanelTab === 'details'"
            :document="currentDocument.meta"
            :user-role="currentDocument.userRole"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
/* These must be un-scoped so the PDF.js viewer can apply them to dynamically inserted DOM */
.document-viewer {
  position: relative;
  overscroll-behavior: inherit !important;
  overflow: scroll !important;
}
.show-scrollbar::-webkit-scrollbar {
  height: 18px;
  position: absolute;
  bottom: 0;
  z-index: 100;
}
.show-scrollbar::-webkit-scrollbar-thumb {
  background-color: #aca7a7;
  border-radius: 10px;
}
.show-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: #555;
}
.page {
  position: relative;
  box-shadow: 0 0 10px 3px #e0e0e0;
}
</style>

<style scoped lang="scss">
.document-container {
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  overflow: hidden;

  .document-header {
    display: flex;
    justify-content: center;
    width: 100%;
    flex-shrink: 0;

    :global(html.dark) & {
      background-color: #1e293b;
      border-color: #334155;
    }
  }

  .document-main {
    position: relative;
    width: 100%;
    flex: 1;
    display: flex;
    overflow: hidden;

    .thumbnail-pages {
      display: none; /* enable later when thumbnail sidebar is built */
    }

    .pdf-viewer {
      flex: 1;
      position: relative;
      overflow: hidden;

      .viewer-container {
        position: absolute;
        inset: 0;
        overflow: auto;
        background: transparent;
      }

      .viewer-loader {
        position: absolute;
        inset: 0;
        background: url("../../../assets/images/loading-icon.gif") center no-repeat;
      }

      .document-zoom {
        position: absolute;
        bottom: 16px;
        left: 16px;
        height: 36px;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px;
        background: rgba(148, 163, 184, 0.2);
        border-radius: 100px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(14px);
        font-weight: 600;
        font-size: 14px;
        color: #64748b;
        user-select: none;

        .zoom-btn {
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          &:hover { background-color: #ebebeb; }
        }
      }

      .document-pages {
        position: absolute;
        bottom: 16px;
        right: 34px;
        height: 36px;
        display: flex;
        align-items: center;
        padding: 8px 12px;
        background: rgba(148, 163, 184, 0.2);
        border-radius: 100px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(14px);
        font-weight: 600;
        font-size: 14px;
        color: #64748b;
        white-space: nowrap;
      }

      .document-loading {
        position: absolute;
        inset: 0;
        background: url("../../../assets/images/loading-icon.gif") center no-repeat;
      }
    }
  }
}

div:focus { outline: none; }
</style>
